/* eslint-disable indent */
import React, { useEffect, useState } from "react";
import {
  renderDisabledNextLink,
  renderDisabledPrevLink,
  renderEnabledNextLink,
  renderEnabledPrevLink,
} from "./pagination.helper";
import PageLink from "./common/PageLink";
import { PaginationPagesProps } from "./pagination-pages";

export const generateTruncatedPageArr = (
  page: number,
  numberOfPages: number
): { pageItems: number[]; prefix?: number; suffix?: number } => {
  // Page starts from 1 till number of pages. Increments by 1 or decrements by 1.
  // Any given time, we have 5 pills displayed
  if (page >= 1 && page <= 3) {
    // the starting pills 1,2,3,4,...10
    return {
      pageItems: [1, 2, 3, 4, numberOfPages],
      prefix: 4,
      suffix: undefined,
    };
  } else if (page < numberOfPages - 1) {
    // pills greater than 3. 1,...4,5,6..., 10
    return {
      pageItems: [1, page - 1, page, page + 1, numberOfPages],
      prefix: page >= numberOfPages - 2 ? undefined : 4,
      suffix: 0,
    };
  } else if (page < numberOfPages) {
    // the last before one pills 1, ...7, 8, 9, 10
    return {
      pageItems: [1, page - 2, page - 1, page, numberOfPages],
      prefix: undefined,
      suffix: 0,
    };
  } else {
    // last pills 1, ...7, 8, 9, 10
    return {
      pageItems: [1, page - 3, page - 2, page - 1, numberOfPages],
      prefix: undefined,
      suffix: 0,
    };
  }
};

/**
 * This component gets rendered all the times and cannot be turned of with a prop.
 *
 * The component uses mathematical calculations to compute number of pageItems to be
 * displayed and to enable/disable previous/next links
 */
export const PaginationTruncatedPages = ({
  page,
  setPage,
  resultsCount,
  pageSize,
  previousLabel,
  previousAriaLabel,
  nextLabel,
  nextAriaLabel,
  ariaItemLabel,
  pageClickHandler,
}: PaginationPagesProps): React.ReactElement => {
  /*
   * Used to compute total pageItems out of which 5 will be shown ideally.
   */
  const numberOfPages = Math.ceil(resultsCount / pageSize);

  const [pagesObj, setPagesObj] = useState(
    generateTruncatedPageArr(page, numberOfPages)
  );

  const [itemStartIndex, setItemStartIndex] = useState(0);

  /*
   * When pageSize is changed in the results per page dropdown, we need to rebuild the
   * page items between prev & next links.
   */
  useEffect(() => {
    setItemStartIndex(0);
    setPagesObj(generateTruncatedPageArr(page, numberOfPages));
  }, [pageSize, page, numberOfPages]);

  /*
   * Conditional checks to enable or disable the next/prev
   */
  const disablePreviousBtn = page === 1;
  const disableNextBtn = page >= numberOfPages;

  /*
   * When next/prev button is clicked, we need to
   * 1. Change the page number (always)
   * 2. Change the startIndex (only if the pageNumber is not in the view)
   * @param type
   */
  const refreshPageNumberAndIndex = (type: "PREVIOUS" | "NEXT"): void => {
    const pageDerived = pagesObj.pageItems.indexOf(page);
    const newPage =
      type === "PREVIOUS"
        ? pagesObj.pageItems[pageDerived - 1]
        : pagesObj.pageItems[pageDerived + 1];

    setPagesObj(generateTruncatedPageArr(newPage, numberOfPages));
    setPage(newPage);
    const newIndex = 0;

    if (type === "NEXT") {
      if (newPage >= itemStartIndex + 5) {
        // 5 -> numberOfPagesToDisplay
        setItemStartIndex(newIndex);
      }
    } else {
      if (newPage < itemStartIndex) {
        setItemStartIndex(newIndex);
      }
    }

    pageClickHandler?.(newPage);
  };

  /*
   * Event handlers for prev and next buttons
   * preventDefault is required to prevent browser redirecting to href="#"
   */
  const prevBtnHandler = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    refreshPageNumberAndIndex("PREVIOUS");
    e.preventDefault();
  };

  const nextBtnHandler = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ): void => {
    refreshPageNumberAndIndex("NEXT");
    e.preventDefault();
  };

  /*
   * Callback that gets executed when pageItem is clicked,
   * preventDefault is required to prevent browser redirecting to href="#"
   */
  const _pageItemClickHandler = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    displayPageNumber: number
  ): void => {
    setPage(displayPageNumber);
    pageClickHandler?.(displayPageNumber);
    e.preventDefault();
  };

  /**
   * The numberOfPagesPerView is computed above and its used to generate anchor tags
   */
  return (
    <div className="spark-pagination__pages">
      {disablePreviousBtn
        ? renderDisabledPrevLink(previousAriaLabel, previousLabel)
        : renderEnabledPrevLink(
            previousAriaLabel,
            previousLabel,
            prevBtnHandler
          )}
      {Array(5)
        .fill(0)
        .map((item, index) => {
          /*
           * Assume next button was clicked once, the itemStartIndex would be 6.
           * The number displayed upon looping would be 6,7,8,9,10
           */
          const displayPageNumber = pagesObj.pageItems[index] + itemStartIndex;

          /*
           * When we only have data for 2 pages, then we do not render the other 3 pages
           */
          if (displayPageNumber > numberOfPages) {
            return null;
          }

          /*
           * Removing href disables keyboard navigation. If developer wants to
           * do operations to URL on pageItem click, it need to be done in the
           * callback prop that gets executed on page item click.
           */
          return (
            <PageLink
              showEllipsisSuffix={index === pagesObj.suffix}
              showEllipsisPrefix={index === pagesObj.prefix}
              displayPageNumber={displayPageNumber}
              page={page}
              ariaItemLabel={ariaItemLabel}
              index={pagesObj.pageItems[index]}
              key={`index${displayPageNumber}${index}`}
              onClick={(e, numberDisplayed): void =>
                _pageItemClickHandler(e, numberDisplayed)
              }
            />
          );
        })}
      {disableNextBtn
        ? renderDisabledNextLink(nextAriaLabel, nextLabel)
        : renderEnabledNextLink(nextAriaLabel, nextLabel, nextBtnHandler)}
    </div>
  );
};
